import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { statusMeta } from '../mock/mock';
import { resolveVariables, extractVariables, buildToken, replaceAllByName } from '../lib/personalize';
import { Plus, ArrowLeft, Send, Play, Pause, Trash2, Clock, Users, UserPlus, AlertTriangle, Mail, Paperclip, Copy, Archive, ArchiveRestore, MoreHorizontal, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';
import AddRecipientsDialog from '../components/AddRecipientsDialog';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import AttachmentsPanel from '../components/AttachmentsPanel';
import { VarInsertMenu, VarChipBar } from '../components/VarUI';
import EditContactDialog from '../components/EditContactDialog';
import TestSequenceDialog from '../components/TestSequenceDialog';
import StepScheduleControls from '../components/StepScheduleControls';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { parseServerDate, formatInTz } from '../lib/datetime';

export function CampaignsList() {
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState('active'); // active | archived | all
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = (f = filter) => {
    setLoading(true);
    api.get(`/campaigns?filter=${f}`).then(r => setCampaigns(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const duplicate = async (e, cid) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const r = await api.post(`/campaigns/${cid}/duplicate`);
      toast.success('Campaign duplicated');
      nav(`/app/campaigns/${r.data.id}`);
    } catch (err) { toast.error(extractErrorMessage(err, 'Duplicate failed')); }
  };
  const archive = async (e, cid) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const r = await api.post(`/campaigns/${cid}/archive`);
      toast.success(`Archived · stopped ${r.data._stopped_sends || 0} scheduled send(s)`);
      load();
    } catch (err) { toast.error(extractErrorMessage(err, 'Archive failed')); }
  };
  const unarchive = async (e, cid) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/campaigns/${cid}/unarchive`);
      toast.success('Campaign restored');
      load();
    } catch (err) { toast.error(extractErrorMessage(err, 'Restore failed')); }
  };

  const filterTabs = [
    { key: 'active',   label: 'Active'   },
    { key: 'archived', label: 'Archived' },
    { key: 'all',      label: 'All'      },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Campaigns</h1>
          <p className="text-zinc-500 mt-1">All your outreach sequences in one place.</p>
        </div>
        <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" asChild>
          <Link to="/app/campaigns/new"><Plus size={16} className="mr-1"/>New campaign</Link>
        </Button>
      </div>

      <div className="mt-6 inline-flex rounded-md border border-zinc-800 bg-[#0a0a0b] p-1" data-testid="campaigns-filter-tabs">
        {filterTabs.map(t => (
          <button
            key={t.key}
            onClick={()=>setFilter(t.key)}
            data-testid={`campaigns-filter-${t.key}`}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              filter === t.key
                ? 'bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18]'
                : 'text-zinc-500 hover:text-zinc-200'
            }`}
          >{t.label}</button>
        ))}
      </div>

      <div className="mt-6 grid gap-4">
        {!loading && campaigns.length === 0 && (
          <Card className="bg-[#111110] border-zinc-900 p-12 text-center">
            <div className="font-display text-xl">{filter === 'archived' ? 'No archived campaigns' : 'No campaigns yet'}</div>
            <p className="text-sm text-zinc-500 mt-2">{filter === 'archived' ? 'Archived campaigns will show up here.' : 'Create your first outreach sequence.'}</p>
            {filter !== 'archived' && (
              <Button className="mt-6 bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" asChild><Link to="/app/campaigns/new">Create campaign</Link></Button>
            )}
          </Card>
        )}
        {campaigns.map(c => {
          const m = c.metrics || {};
          const isArchived = !!c.archived;
          return (
            <div key={c.id} className="relative group">
              <Link to={`/app/campaigns/${c.id}`}>
                <Card className={`bg-[#111110] border-zinc-900 hover:border-[#3a2f18] transition-colors p-5 ${isArchived ? 'opacity-70' : ''}`} data-testid={`campaign-row-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="font-display text-xl">{c.name}</div>
                        <Badge className={c.status==='active' ? 'bg-emerald-950 text-emerald-300 border border-emerald-900' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}>{c.status}</Badge>
                        {isArchived && (
                          <Badge className="bg-zinc-900 text-zinc-500 border border-zinc-800"><Archive size={10} className="mr-1"/>archived</Badge>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">{c.contact_count} contacts · {c.steps?.length || 0} steps</div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="grid grid-cols-4 gap-6 text-right">
                        <div><div className="text-xs text-zinc-500">Sent</div><div className="text-sm text-zinc-200">{m.sent||0}</div></div>
                        <div><div className="text-xs text-zinc-500">Opens</div><div className="text-sm text-zinc-200">{m.opens||0}</div></div>
                        <div><div className="text-xs text-zinc-500">Replies</div><div className="text-sm text-[#f5d67a]">{m.replies||0}</div></div>
                        <div><div className="text-xs text-zinc-500">Bounces</div><div className="text-sm text-red-400">{m.bounces||0}</div></div>
                      </div>
                      {/* Reserve horizontal room for action buttons; they overlay on hover */}
                      <div className="w-[110px]"/>
                    </div>
                  </div>
                </Card>
              </Link>
              <div className="absolute top-1/2 -translate-y-1/2 right-5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e)=>duplicate(e, c.id)}
                  data-testid={`campaign-duplicate-${c.id}`}
                  title="Duplicate — copies steps, resets recipients/stats"
                  className="p-2 rounded-md bg-[#0a0a0b] border border-zinc-800 text-zinc-300 hover:text-[#f5d67a] hover:border-[#3a2f18]"
                ><Copy size={14}/></button>
                {isArchived ? (
                  <button
                    onClick={(e)=>unarchive(e, c.id)}
                    data-testid={`campaign-unarchive-${c.id}`}
                    title="Restore from archive"
                    className="p-2 rounded-md bg-[#0a0a0b] border border-zinc-800 text-zinc-300 hover:text-[#f5d67a] hover:border-[#3a2f18]"
                  ><ArchiveRestore size={14}/></button>
                ) : (
                  <button
                    onClick={(e)=>archive(e, c.id)}
                    data-testid={`campaign-archive-${c.id}`}
                    title="Archive — hides and stops all scheduled sends"
                    className="p-2 rounded-md bg-[#0a0a0b] border border-zinc-800 text-zinc-300 hover:text-orange-300 hover:border-orange-900/70"
                  ><Archive size={14}/></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function contactToPreview(c) {
  return {
    first_name: c?.first_name || '', last_name: c?.last_name || '',
    company: c?.company || '', job_title: c?.job_title || '', email: c?.email || '',
  };
}

export function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === 'new';
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [previewId, setPreviewId] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attachmentsInfo, setAttachmentsInfo] = useState({ items: [], count: 0 });
  const [events, setEvents] = useState([]);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [testSeqOpen, setTestSeqOpen] = useState(false);
  const [saveState, setSaveState] = useState({ status: 'idle', at: null });
  const dirtyRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const hydratedRef = useRef(false);
  const { user } = useAuth();
  const subjectRefs = useRef({});
  const bodyRefs = useRef({});

  // Debounced auto-save. Fires 1500ms after the last edit, only on existing
  // campaigns (isNew campaigns must be explicitly Created first). Live edits
  // to campaign name / steps / thread_replies mark dirty. Editing the campaign
  // does NOT retroactively change what has already been sent to any contact —
  // sent copies are captured in the events log; only future scheduled sends
  // pick up the latest step content.
  useEffect(() => {
    if (isNew || !campaign || !hydratedRef.current) return;
    dirtyRef.current = true;
    setSaveState(s => ({ ...s, status: 'dirty' }));
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      save({ auto: true });
      dirtyRef.current = false;
    }, 1500);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.name, JSON.stringify(campaign?.steps), campaign?.thread_replies]);

  const loadCampaign = async () => {
    if (isNew) {
      setCampaign({ id: 'new', name: 'New campaign', status: 'draft', thread_replies: true, steps: [{ id:'s_new', order:1, delay_days:0, subject:'', body:'' }], metrics: {sent:0,opens:0,replies:0,bounces:0}, contact_count: 0 });
      setRecipients([]);
      setAttachmentsInfo({ items: [], count: 0 });
    } else {
      try {
        const r = await api.get(`/campaigns/${id}`);
        setCampaign(r.data);
        // Mark hydrated on the next tick so the auto-save effect doesn't fire
        // for the initial load.
        setTimeout(() => { hydratedRef.current = true; }, 0);
      } catch { toast.error('Campaign not found'); nav('/app/campaigns'); }
      try {
        const cr = await api.get('/contacts', { params: { campaign_id: id } });
        setRecipients(cr.data);
        if (cr.data.length > 0 && !previewId) setPreviewId(cr.data[0].id);
      } catch (err) { console.warn('Load recipients failed:', err); }
      try {
        const ar = await api.get(`/campaigns/${id}/attachments`);
        setAttachmentsInfo({ items: ar.data.attachments || [], count: (ar.data.attachments || []).length });
      } catch (err) { console.warn('Load attachments failed:', err); }
    }
  };
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsUpdatedAt, setEventsUpdatedAt] = useState(null);
  const loadEvents = async () => {
    if (isNew) return;
    setEventsLoading(true);
    try {
      const r = await api.get(`/campaigns/${id}/events`);
      setEvents(r.data || []);
      setEventsUpdatedAt(new Date());
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not load recent activity'));
    } finally {
      setEventsLoading(false);
    }
  };
  useEffect(() => { loadCampaign(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [id]);

  if (!campaign) return <div className="p-8 text-zinc-500">Loading…</div>;

  const selectedPreview = recipients.find(r => r.id === previewId) || (recipients[0]) || { first_name: 'Jamie', last_name: 'Chen', company: 'Stripe', job_title: 'Senior Recruiter' };

  const updateStep = (idx, field, value) => setCampaign(c => ({...c, steps: c.steps.map((s,i) => i===idx ? {...s, [field]: value} : s)}));
  const addStep = () => setCampaign(c => ({...c, steps: [...c.steps, { order: c.steps.length+1, delay_days: 3, delay_hours: 0, delay_minutes: 0, subject:'', body:'' }]}));
  const removeStep = (idx) => setCampaign(c => ({...c, steps: c.steps.filter((_,i)=>i!==idx)}));

  const insertAt = (which, idx, token) => {
    const refs = which === 'subject' ? subjectRefs.current : bodyRefs.current;
    const el = refs[idx];
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const newVal = before + token + after;
    updateStep(idx, which, newVal);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async (opts = {}) => {
    const isAuto = !!opts.auto;
    try {
      if (isAuto) setSaveState({ status: 'saving', at: null });
      const payload = {
        name: campaign.name,
        status: campaign.status,
        thread_replies: campaign.thread_replies !== false,
        steps: campaign.steps.map((s,i)=>({
          order:i+1,
          delay_days: parseInt(s.delay_days)||0,
          delay_hours: parseInt(s.delay_hours)||0,
          delay_minutes: parseInt(s.delay_minutes)||0,
          subject: s.subject||'',
          body: s.body||'',
          send_time_of_day: s.send_time_of_day || null,
          send_days: (Array.isArray(s.send_days) && s.send_days.length) ? s.send_days : null,
        })),
      };
      if (isNew) {
        const r = await api.post('/campaigns', payload);
        toast.success('Campaign created');
        nav(`/app/campaigns/${r.data.id}`);
      } else {
        const r = await api.patch(`/campaigns/${campaign.id}`, payload);
        // Preserve the freshly-typed values; only merge server-side fields.
        setCampaign(prev => ({ ...prev, ...r.data, name: prev.name, steps: prev.steps, thread_replies: prev.thread_replies }));
        setSaveState({ status: 'saved', at: new Date() });
        if (!isAuto) toast.success('Campaign saved');
      }
    } catch (err) {
      setSaveState({ status: 'error', at: null, error: extractErrorMessage(err, 'Save failed') });
      if (!isAuto) toast.error(extractErrorMessage(err, 'Save failed'));
    }
  };

  const toggle = async () => {
    if (isNew) return toast.error('Save the campaign first');
    try {
      const r = await api.post(`/campaigns/${campaign.id}/toggle`);
      setCampaign(r.data);
      toast.success(r.data.status === 'active' ? 'Campaign started' : 'Campaign paused');
    } catch { toast.error('Toggle failed'); }
  };

  const processQueue = async () => {
    if (isNew) return toast.error('Save first');
    try {
      const r = await api.post(`/campaigns/${campaign.id}/process-queue`);
      const d = r.data || {};
      const failures = d.failures || [];
      const okMsg = d.simulated > 0
        ? `${d.sent} sent (${d.simulated} simulated — connect Outlook for real send)`
        : `${d.sent} sent`;
      if (failures.length > 0) {
        const first = failures[0];
        toast.error(`${failures.length} failed: ${first.name || first.email || 'contact'} — ${first.detail || first.reason}`, { duration: 8000 });
      }
      if (d.sent > 0 || d.simulated > 0) toast.success(okMsg);
      if (d.processed === 0 && failures.length === 0) {
        // Explain WHY nothing was due.
        let msg = 'Nothing due to send right now';
        const reason = d.nothing_due_reason;
        if (reason === 'scheduled_later' && d.next_scheduled_at) {
          const nice = formatInTz(d.next_scheduled_at, user?.timezone);
          msg = `Nothing due — ${d.next_scheduled_count} contact${d.next_scheduled_count === 1 ? '' : 's'} scheduled for ${nice}`;
        } else if (reason === 'sequence_complete') {
          msg = 'Nothing due — sequence complete for every contact';
        } else if (reason === 'missing_data_only') {
          msg = `Nothing due — ${d.missing_data_count} contact${d.missing_data_count === 1 ? '' : 's'} blocked by missing data (edit them to unblock)`;
        } else if (reason === 'no_contacts') {
          msg = 'Nothing due — no contacts on this campaign yet';
        }
        toast(msg, { icon: '⏰', duration: 6000 });
      }
      if (d.throttled) {
        toast(`Daily cap reached — ${d.sent_today}/${d.daily_cap} sent today. Resumes at UTC midnight or from Settings.`, { icon: '⬛', duration: 8000 });
      } else if (typeof d.remaining_today === 'number' && d.remaining_today <= 5 && d.remaining_today >= 0) {
        toast(`${d.remaining_today} sends left today (cap ${d.daily_cap}).`, { icon: '⏳' });
      }
      if (d.replies_found > 0) toast.success(`${d.replies_found} new ${d.replies_found === 1 ? 'reply' : 'replies'} detected — sequences paused`);
      if (d.bounces_found > 0) toast.error(`${d.bounces_found} bounce(s) detected`);
      if (d.reply_check_error && d.reply_check_error !== 'not_connected') {
        console.warn('Reply check error:', d.reply_check_error);
      }
      loadCampaign();
      loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Queue processing failed'));
    }
  };

  const checkReplies = async () => {
    if (isNew) return;
    try {
      const r = await api.post(`/campaigns/${campaign.id}/check-replies`);
      const d = r.data || {};
      if (d.error) return toast.error(`Reply check failed: ${d.error.slice(0, 200)}`, { duration: 10000 });
      const bits = [];
      bits.push(`${d.replies || 0} new ${d.replies === 1 ? 'reply' : 'replies'}`);
      bits.push(`${d.bounces || 0} bounce${d.bounces === 1 ? '' : 's'}`);
      bits.push(`scanned ${d.checked_messages || 0} msgs`);
      if (d.matched_by_conversation || d.matched_by_sender) {
        bits.push(`matched: ${d.matched_by_conversation || 0} by convId + ${d.matched_by_sender || 0} by sender`);
      }
      toast.success(bits.join(' · '), { duration: 8000 });
      if ((d.unmatched_samples || []).length > 0 && (d.replies || 0) === 0) {
        // Give a hint about why nothing matched, one message.
        const first = d.unmatched_samples[0];
        toast(`No matches — first skipped: "${(first.subject || '(no subject)').slice(0, 40)}" from ${first.from || '?'} (reason: ${first.reason})`, { icon: '🔍', duration: 10000 });
      }
      loadCampaign();
      loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Reply check failed'));
    }
  };

  const runDiagnose = async () => {
    try {
      const r = await api.get('/outlook/diagnose');
      const d = r.data || {};
      if (d.token_ok) {
        toast.success(`Outlook auth ok — ${d.outlook_email || 'account'} can send. (mail probe HTTP ${d.mail_probe?.status})`, { duration: 6000 });
      } else {
        const detail = d.mail_probe?.body?.slice(0,200) || d.error || 'unknown';
        toast.error(`Outlook auth failed (HTTP ${d.mail_probe?.status || '?'}): ${detail}`, { duration: 10000 });
      }
    } catch (err) { toast.error('Diagnose call failed'); }
  };

  const runTestSend = async () => {
    const to = window.prompt('Send a plain test email (no attachment) to which address?', user?.email || '');
    if (!to) return;
    try {
      const r = await api.post('/outlook/test-send', { to_email: to });
      if (r.data.ok) toast.success(`Test email sent to ${to}`);
      else toast.error(`Test send failed: ${r.data.error || r.data.graph_body?.slice(0, 200) || 'unknown'}`, { duration: 10000 });
    } catch (err) { toast.error(extractErrorMessage(err, 'Test send failed')); }
  };

  const removeRecipient = async (contactId) => {
    try { await api.delete(`/campaigns/${campaign.id}/contacts/${contactId}`); loadCampaign(); toast.success('Removed'); }
    catch { toast.error('Failed to remove'); }
  };

  // Missing-var analysis across recipients for current step
  const missingReport = (() => {
    if (isNew || recipients.length === 0) return { count: 0, byContact: {} };
    const byContact = {};
    let count = 0;
    for (const r of recipients) {
      const allMissing = new Set();
      for (const s of campaign.steps) {
        const { missing: mS } = resolveVariables(s.subject || '', contactToPreview(r));
        const { missing: mB } = resolveVariables(s.body || '', contactToPreview(r));
        mS.forEach(x=>allMissing.add(x)); mB.forEach(x=>allMissing.add(x));
      }
      if (allMissing.size > 0) { byContact[r.id] = Array.from(allMissing); count++; }
    }
    return { count, byContact };
  })();

  const duplicate = async () => {
    if (isNew) return toast.error('Save the campaign first');
    try {
      const r = await api.post(`/campaigns/${campaign.id}/duplicate`);
      toast.success('Duplicated — opening the copy');
      nav(`/app/campaigns/${r.data.id}`);
    } catch (err) { toast.error(extractErrorMessage(err, 'Duplicate failed')); }
  };
  const archive = async () => {
    if (isNew) return;
    try {
      const r = await api.post(`/campaigns/${campaign.id}/archive`);
      toast.success(`Archived · stopped ${r.data._stopped_sends || 0} scheduled send(s)`);
      loadCampaign();
    } catch (err) { toast.error(extractErrorMessage(err, 'Archive failed')); }
  };
  const unarchive = async () => {
    if (isNew) return;
    try {
      await api.post(`/campaigns/${campaign.id}/unarchive`);
      toast.success('Restored — you can launch it again from here');
      loadCampaign();
    } catch (err) { toast.error(extractErrorMessage(err, 'Restore failed')); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <Link to="/app/campaigns" className="text-sm text-zinc-500 hover:text-[#f5d67a] flex items-center gap-1"><ArrowLeft size={14}/>Back to campaigns</Link>
      <div className="mt-4 flex items-center justify-between gap-4">
        <Input value={campaign.name} onChange={e=>setCampaign({...campaign, name:e.target.value})} className="bg-transparent border-none text-3xl font-display px-0 focus-visible:ring-0 h-auto text-zinc-100"/>
        <div className="flex gap-2 flex-wrap justify-end">
          {!isNew && (
            <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" onClick={duplicate} data-testid="campaign-detail-duplicate">
              <Copy size={14} className="mr-1"/>Duplicate
            </Button>
          )}
          {!isNew && (campaign.archived ? (
            <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" onClick={unarchive} data-testid="campaign-detail-unarchive">
              <ArchiveRestore size={14} className="mr-1"/>Restore
            </Button>
          ) : (
            <Button variant="outline" className="border-zinc-800 text-orange-300 hover:bg-orange-950/40 hover:border-orange-900/70" onClick={archive} data-testid="campaign-detail-archive">
              <Archive size={14} className="mr-1"/>Archive
            </Button>
          ))}
          <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={save} data-testid="campaign-save-btn">{isNew ? 'Create' : 'Save'}</Button>
          {!isNew && !campaign.archived && (() => {
            const dot =
              saveState.status === 'saving' ? 'bg-[#f5d67a] animate-pulse' :
              saveState.status === 'saved'  ? 'bg-emerald-400' :
              saveState.status === 'error'  ? 'bg-red-400' :
              saveState.status === 'dirty'  ? 'bg-zinc-500' :
              'bg-zinc-700';
            const label =
              saveState.status === 'saving' ? 'Saving\u2026' :
              saveState.status === 'saved'  ? (saveState.at ? `Saved ${saveState.at.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit',second:'2-digit'})}` : 'Saved') :
              saveState.status === 'error'  ? 'Save failed — retrying next edit' :
              saveState.status === 'dirty'  ? 'Unsaved changes\u2026' :
              '';
            return label ? (
              <div
                className="text-[11px] text-zinc-400 flex items-center gap-1.5 self-center"
                title={saveState.status === 'saved' ? 'Auto-saved. Edits only affect FUTURE scheduled sends — already-sent copies live in Recent Activity.' : (saveState.error || '')}
                data-testid="autosave-indicator"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`}/>
                {label}
              </div>
            ) : null;
          })()}
          {!isNew && !campaign.archived && <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={toggle} data-testid="campaign-launch-btn">
            {campaign.status==='active' ? <><Pause size={14} className="mr-1"/>Pause</> : <><Play size={14} className="mr-1"/>Launch</>}
          </Button>}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <Badge className={campaign.status==='active'?'bg-emerald-950 text-emerald-300 border border-emerald-900':'bg-zinc-900 text-zinc-400 border border-zinc-800'}>{campaign.status}</Badge>
        {campaign.archived && (
          <Badge className="bg-zinc-900 text-zinc-500 border border-zinc-800"><Archive size={10} className="mr-1"/>archived</Badge>
        )}
        <span className="text-xs text-zinc-500">{recipients.length} recipients · {campaign.steps.length} steps</span>
      </div>

      {campaign.archived && (
        <Card className="mt-4 bg-orange-950/20 border-orange-900/70 p-4 text-sm text-orange-200">
          This campaign is <strong>archived</strong>. All scheduled sends were halted and it's hidden from the main list. Click <strong>Restore</strong> above to make it editable and launchable again.
        </Card>
      )}

      {/* SEQUENCE SETTINGS */}
      <Card className="mt-4 bg-[#111110] border-zinc-900 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-zinc-100 font-medium">Reply in same thread for follow-ups</div>
            <div className="text-xs text-zinc-500 mt-0.5">Follow-ups will be sent as a reply to the initial email so they thread in the recipient's inbox (recommended).</div>
          </div>
          <Switch
            checked={campaign.thread_replies !== false}
            onCheckedChange={(v)=>setCampaign(c=>({...c, thread_replies: !!v}))}
            data-testid="thread-replies-toggle"
            className="data-[state=checked]:bg-[#d4a94a]"
          />
        </div>
      </Card>

      {!isNew && (
        <div className="grid grid-cols-4 gap-4 mt-6">
          {[{l:'Sent',v:campaign.metrics?.sent||0},{l:'Opens',v:campaign.metrics?.opens||0},{l:'Replies',v:campaign.metrics?.replies||0},{l:'Bounces',v:campaign.metrics?.bounces||0}].map(s=>(
            <Card key={s.l} className="bg-[#111110] border-zinc-900 p-4">
              <div className="text-xs text-zinc-500">{s.l}</div>
              <div className="mt-2 font-display text-2xl">{s.v}</div>
            </Card>
          ))}
        </div>
      )}

      {/* RECIPIENTS */}
      {!isNew && (
        <Card className="mt-8 bg-[#111110] border-zinc-900">
          <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display text-lg flex items-center gap-2"><Users size={16} className="text-[#d4a94a]"/>Recipients</div>
              <div className="text-xs text-zinc-500 mt-1">{recipients.length} in this campaign{missingReport.count>0 ? ` · ${missingReport.count} missing data` : ''}</div>
            </div>
            <div className="flex items-center gap-2">
              {recipients.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Mail size={12}/>Preview as:
                  <Select value={previewId || recipients[0]?.id} onValueChange={setPreviewId}>
                    <SelectTrigger className="w-[200px] h-8 bg-[#0a0a0b] border-zinc-800 text-zinc-100 text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#111110] border-zinc-800 text-zinc-100 text-xs">
                      {recipients.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.first_name} {r.last_name} {r.company ? '· '+r.company : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button size="sm" className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={()=>setPickerOpen(true)}><UserPlus size={14} className="mr-1"/>Add recipients</Button>
            </div>
          </div>
          {recipients.length === 0 ? (
            <div className="p-10 text-center">
              <div className="font-display text-lg text-zinc-300">No recipients yet</div>
              <p className="text-sm text-zinc-500 mt-1">Pick from your contacts, import from a file, or add one manually.</p>
              <Button className="mt-5 bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={()=>setPickerOpen(true)}><UserPlus size={14} className="mr-1"/>Add recipients</Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {recipients.map(r => {
                const m = statusMeta[r.status] || statusMeta.queued;
                const miss = missingReport.byContact[r.id];
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between px-5 py-3 hover:bg-zinc-900/30 cursor-pointer"
                    onClick={()=>setEditingRecipient(r)}
                    data-testid={`recipient-row-${r.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-zinc-100">{r.first_name} {r.last_name}</span>
                        {miss && <Badge className="bg-orange-950 text-orange-300 border border-orange-900 text-[10px]"><AlertTriangle size={10} className="mr-1"/>missing: {miss.join(', ')}</Badge>}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{r.email}{r.company ? ` · ${r.company}` : ''}{r.job_title ? ` · ${r.job_title}` : ''}</div>
                      {r.next_send_at && ['queued','awaiting_followup'].includes(r.status) && (() => {
                        const dt = parseServerDate(r.next_send_at);
                        if (!dt) return null;
                        const tz = user?.timezone || undefined;
                        const nice = formatInTz(r.next_send_at, tz);
                        const stepIdx = (r.current_step_index || 0);
                        const stepLabel = stepIdx === 0 ? 'Initial email' : `Follow-up ${stepIdx}`;
                        return (
                          <div className="text-[11px] text-[#f5d67a]/80 mt-0.5" title={`Stored UTC: ${dt.toISOString()} — displayed in ${tz || 'browser local time'}`} data-testid={`recipient-next-send-${r.id}`}>
                            <Clock size={10} className="inline mr-1 -mt-0.5"/>{stepLabel} · {nice}
                          </div>
                        );
                      })()}
                      {r.last_error && <div className="text-xs text-red-300 mt-0.5 truncate" title={r.last_error}>send failed: {r.last_error}</div>}
                    </div>
                    <Badge className={`${m.color} text-[10px] mr-3`}>{m.label}</Badge>
                    <button onClick={(e)=>{ e.stopPropagation(); removeRecipient(r.id); }} className="text-zinc-500 hover:text-red-400" data-testid={`recipient-remove-${r.id}`}><Trash2 size={14}/></button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* STEPS */}
      <div className="mt-8 space-y-4">
        {campaign.steps.map((s, idx) => {
          const previewContact = contactToPreview(selectedPreview);
          const threadOn = campaign.thread_replies !== false;
          const isThreadedFollowup = threadOn && idx > 0;
          const initialSubject = (campaign.steps[0]?.subject || '').trim();
          const effectiveSubject = isThreadedFollowup
            ? `Re: ${initialSubject || '(set the initial subject)'}`
            : s.subject;
          const subjectForResolve = isThreadedFollowup ? effectiveSubject : (s.subject || '');
          const subjectResolved = resolveVariables(subjectForResolve, previewContact);
          const bodyResolved = resolveVariables(s.body || '', previewContact);
          const bodyVars = extractVariables(s.body || '');
          const subjectVars = isThreadedFollowup ? [] : extractVariables(s.subject || '');

          const updateFallback = (field) => (v, newFb) => {
            const newToken = buildToken(v.name, newFb);
            updateStep(idx, field, replaceAllByName(s[field] || '', v.name, newToken));
          };
          const removeVarInText = (field) => (v) => {
            const re = new RegExp('\\{{1,2}' + v.name + '(?:[:|][^{}]*)?\\}{1,2}', 'g');
            updateStep(idx, field, (s[field] || '').replace(re, ''));
          };

          return (
            <Card key={idx} className="bg-[#111110] border-zinc-900 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#1a1815] border border-[#3a2f18] text-[#f5d67a] flex items-center justify-center text-sm">{idx+1}</div>
                  <div className="font-display text-lg">{idx===0 ? 'Initial email' : `Follow-up ${idx}`}</div>
                  {idx>0 && (() => {
                    // Determine currently-active delay unit from the step data.
                    const dm = parseInt(s.delay_minutes)||0;
                    const dh = parseInt(s.delay_hours)||0;
                    const activeUnit = dm > 0 ? 'minutes' : dh > 0 ? 'hours' : 'days';
                    const activeValue = dm > 0 ? dm : dh > 0 ? dh : (parseInt(s.delay_days)||0);
                    const setUnit = (unit) => {
                      // Clear all delay fields then set the chosen one.
                      const patch = { delay_days: 0, delay_hours: 0, delay_minutes: 0 };
                      const v = Math.max(1, activeValue || 1);
                      if (unit === 'minutes') patch.delay_minutes = v;
                      else if (unit === 'hours') patch.delay_hours = v;
                      else patch.delay_days = v;
                      setCampaign(c => ({...c, steps: c.steps.map((x,i)=>i===idx?{...x,...patch}:x)}));
                    };
                    const setVal = (v) => {
                      const n = parseInt(v)||0;
                      const patch = { delay_days: 0, delay_hours: 0, delay_minutes: 0 };
                      if (activeUnit === 'minutes') patch.delay_minutes = n;
                      else if (activeUnit === 'hours') patch.delay_hours = n;
                      else patch.delay_days = n;
                      setCampaign(c => ({...c, steps: c.steps.map((x,i)=>i===idx?{...x,...patch}:x)}));
                    };
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <Clock size={12}/>Wait
                        <Input
                          type="number" min={1} value={activeValue}
                          onChange={e=>setVal(e.target.value)}
                          className="w-16 h-7 bg-[#0a0a0b] border-zinc-800 text-zinc-100 text-center"
                          data-testid={`step-${idx}-delay-value`}
                        />
                        <Select value={activeUnit} onValueChange={setUnit}>
                          <SelectTrigger className="h-7 w-[100px] bg-[#0a0a0b] border-zinc-800 text-zinc-200 text-xs" data-testid={`step-${idx}-delay-unit`}>
                            <SelectValue/>
                          </SelectTrigger>
                          <SelectContent className="bg-[#111110] border-zinc-800 text-zinc-100 text-xs">
                            <SelectItem value="minutes">minutes</SelectItem>
                            <SelectItem value="hours">hours</SelectItem>
                            <SelectItem value="days">days</SelectItem>
                          </SelectContent>
                        </Select>
                        {activeUnit !== 'days' && (
                          <span className="text-[10px] text-[#f5d67a] uppercase tracking-wider ml-1">test mode</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {campaign.steps.length > 1 && (
                  <button onClick={()=>removeStep(idx)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14}/></button>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* EDITOR */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs text-zinc-500">Subject</label>
                    {!isThreadedFollowup && <VarInsertMenu onInsert={(tok)=>insertAt('subject', idx, tok)}/>}
                  </div>
                  {isThreadedFollowup ? (
                    <div
                      className="h-11 px-3 flex items-center rounded-md bg-[#0a0a0b] border border-zinc-800 text-zinc-400 text-[15px] italic"
                      data-testid={`followup-subject-inherited-${idx}`}
                      title="This follow-up is sent as a reply to the initial email, so its subject is inherited from Microsoft Graph."
                    >
                      <span className="text-zinc-500 mr-2">Re:</span>
                      <span className="truncate">{initialSubject || <span className="text-zinc-600">(set the initial email subject first)</span>}</span>
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-[#f5d67a]">threaded</span>
                    </div>
                  ) : (
                    <Input placeholder="e.g. Interested in {{job_title|the role}} at {{company|your company}}" value={s.subject} onChange={e=>updateStep(idx,'subject',e.target.value)} ref={el=>subjectRefs.current[idx]=el} className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] text-[15px] h-11"/>
                  )}
                  {isThreadedFollowup && (
                    <div className="mt-1 text-[11px] text-zinc-500">
                      Inherited from step 1 because <span className="text-zinc-300">Reply in same thread</span> is on. Turn it off to give this follow-up its own subject.
                    </div>
                  )}
                  {subjectVars.length > 0 && (
                    <div className="mt-2">
                      <VarChipBar variables={subjectVars} onUpdate={updateFallback('subject')} onRemove={removeVarInText('subject')}/>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4 mb-1.5">
                    <label className="text-xs text-zinc-500">Body</label>
                    <VarInsertMenu onInsert={(tok)=>insertAt('body', idx, tok)}/>
                  </div>
                  <AutoGrowTextarea
                    minRows={14}
                    placeholder={`Hi {{first_name|there}},\n\nI came across {{company|your company}} and would love to chat about the {{job_title|role}}…`}
                    value={s.body}
                    onChange={e=>updateStep(idx,'body',e.target.value)}
                    ref={el=>bodyRefs.current[idx]=el}
                  />
                  {bodyVars.length > 0 && (
                    <div className="mt-2">
                      <VarChipBar variables={bodyVars} onUpdate={updateFallback('body')} onRemove={removeVarInText('body')}/>
                    </div>
                  )}
                  <div className="mt-2 text-xs text-zinc-500">Click any chip to edit its fallback. Emails with unresolved required variables are automatically skipped and flagged.</div>
                  <StepScheduleControls
                    value={{ send_time_of_day: s.send_time_of_day, send_days: s.send_days }}
                    timezone={user?.timezone}
                    onChange={(patch) => setCampaign(c => ({ ...c, steps: c.steps.map((x,i)=>i===idx ? {...x, ...patch} : x) }))}
                  />
                </div>

                {/* PREVIEW */}
                <div className="rounded-lg border border-zinc-800 bg-[#0a0a0b] overflow-hidden">
                  <div className="px-4 py-2 border-b border-zinc-900 text-[11px] uppercase tracking-wider text-zinc-500 flex items-center justify-between">
                    <span>Preview — as {selectedPreview.first_name} {selectedPreview.last_name}{selectedPreview.company ? ` · ${selectedPreview.company}` : ''}</span>
                    {attachmentsInfo.count > 0 && (
                      <span className="flex items-center gap-1 text-zinc-400"><Paperclip size={11}/>{attachmentsInfo.count}</span>
                    )}
                  </div>
                  <div className="px-4 pt-4 pb-2 text-[15px] text-zinc-100 font-medium leading-snug">
                    {subjectForResolve ? subjectResolved.text : <span className="text-zinc-600">Subject preview…</span>}
                  </div>
                  <div className="px-4 py-3 text-[15px] text-zinc-200 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", minHeight: '180px' }}>
                    {s.body ? bodyResolved.text : <span className="text-zinc-600">Body preview…</span>}
                  </div>
                  {attachmentsInfo.items.length > 0 && (
                    <div className="px-4 py-3 border-t border-zinc-900 space-y-1.5">
                      {attachmentsInfo.items.map(a => (
                        <div key={a.id} className="flex items-center gap-2 text-xs text-zinc-300">
                          <Paperclip size={12} className="text-zinc-500"/>
                          <span className="truncate">{a.filename}</span>
                          <span className="text-zinc-600 ml-auto">{Math.round((a.size || 0) / 1024)} KB</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(subjectResolved.missing.length + bodyResolved.missing.length) > 0 && (
                    <div className="px-4 py-3 border-t border-zinc-900 text-xs text-orange-300 flex items-start gap-2">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
                      <span>This recipient is missing: <span className="font-mono">{[...subjectResolved.missing, ...bodyResolved.missing].join(', ')}</span>. Click a chip above to add a fallback, or they’ll be flagged as “missing data” and skipped.</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        <button onClick={addStep} className="w-full py-4 rounded-lg border border-dashed border-zinc-800 text-zinc-500 hover:border-[#d4a94a] hover:text-[#f5d67a] transition-colors flex items-center justify-center gap-2">
          <Plus size={14}/>Add follow-up step
        </button>
      </div>

      {/* ATTACHMENTS */}
      <div className="mt-6">
        <AttachmentsPanel campaignId={campaign.id}/>
      </div>

      {!isNew && (
        <Card className="mt-8 bg-[#111110] border-zinc-900 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg">Ready to send</div>
              <div className="text-xs text-zinc-500 mt-1">Auto-scheduled every 15 min. Or process now.</div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" onClick={runDiagnose}>Diagnose Outlook</Button>
              <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" onClick={runTestSend}>Send test</Button>
              <Button
                variant="outline"
                className="border-[#3a2f18] text-[#f5d67a] hover:bg-[#1a1815]"
                onClick={()=>setTestSeqOpen(true)}
                data-testid="test-sequence-btn"
              >Test sequence</Button>
              <Button variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" onClick={checkReplies}>Check for replies</Button>
              <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={processQueue}><Send size={14} className="mr-1"/>Process queue now</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Recent send activity — surfaces per-contact Graph errors */}
      {!isNew && (
        <Card className="mt-4 bg-[#111110] border-zinc-900">
          <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display text-lg">Recent activity</div>
              <div className="text-xs text-zinc-500 mt-1">
                Latest send events with Graph errors surfaced per contact.
                {eventsUpdatedAt && (
                  <span className="ml-2 text-zinc-600" data-testid="events-updated-at">
                    · updated {eventsUpdatedAt.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={loadEvents}
              disabled={eventsLoading}
              data-testid="events-refresh-btn"
              className="text-xs text-[#f5d67a] hover:underline disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <RefreshCw size={12} className={eventsLoading ? 'animate-spin' : ''}/>
              {eventsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {events.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No sends yet.</div>
          ) : (
            <div className="divide-y divide-zinc-900 max-h-[360px] overflow-y-auto">
              {events.map((e, i) => {
                const isOk = !!e.ok;
                const isSkipped = !!e.skipped;
                const isSim = !!e.simulated;
                const dot = isOk ? (isSim ? 'bg-zinc-500' : 'bg-emerald-500') : (isSkipped ? 'bg-orange-500' : 'bg-red-500');
                const label = isOk ? (isSim ? 'Simulated' : 'Sent') : (isSkipped ? 'Skipped' : 'Failed');
                const recipient = recipients.find(r => r.id === e.contact_id);
                const evKey = e.id || `${e.contact_id || 'x'}-${e.sent_at || ''}-${i}`;
                return (
                  <div key={evKey} className="px-5 py-3 flex items-start gap-3">
                    <span className={`mt-1.5 w-2 h-2 rounded-full ${dot}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-zinc-100">{recipient ? `${recipient.first_name} ${recipient.last_name}` : (e.contact_id || 'contact')}</span>
                        <span className="text-xs text-zinc-500">{recipient?.email || ''}</span>
                        <Badge className={`text-[10px] ${isOk ? (isSim ? 'bg-zinc-900 text-zinc-400 border-zinc-800' : 'bg-emerald-950 text-emerald-300 border border-emerald-900') : (isSkipped ? 'bg-orange-950 text-orange-300 border border-orange-900' : 'bg-red-950 text-red-300 border border-red-900')}`}>{label}</Badge>
                        {e.graph_status && <span className="text-[10px] text-zinc-500">HTTP {e.graph_status}</span>}
                      </div>
                      <div className="text-xs text-zinc-400 truncate mt-0.5">{e.subject}</div>
                      {e.error && <div className="text-xs text-red-300 mt-1 whitespace-pre-wrap break-words">{e.error}</div>}
                      {e.missing_fields && e.missing_fields.length > 0 && <div className="text-xs text-orange-300 mt-1">Missing: {e.missing_fields.join(', ')}</div>}
                      <div className="text-[10px] text-zinc-600 mt-0.5" title={`Stored UTC: ${(parseServerDate(e.sent_at) || new Date()).toISOString()}`}>{formatInTz(e.sent_at, user?.timezone)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {!isNew && (
        <AddRecipientsDialog open={pickerOpen} onOpenChange={setPickerOpen} campaignId={campaign.id} onAdded={loadCampaign}/>
      )}
      <EditContactDialog
        open={!!editingRecipient}
        onOpenChange={(o)=>{ if (!o) setEditingRecipient(null); }}
        contact={editingRecipient}
        missingFields={editingRecipient ? (missingReport.byContact[editingRecipient.id] || editingRecipient.missing_fields || []) : []}
        onSaved={(updated)=>{ setRecipients(rs=>rs.map(x=>x.id===updated.id ? updated : x)); }}
      />
      {!isNew && (
        <TestSequenceDialog
          open={testSeqOpen}
          onOpenChange={setTestSeqOpen}
          campaign={campaign}
          defaultEmail={user?.email || ''}
        />
      )}
    </div>
  );
}
